import {useCallback, useRef} from 'react'
import moment from 'moment'
import useApi from '../../../services/useApi'
import useFilters from '../../../hook/useFilters'
import {MONTHS_SHORT} from '../../../utils/date'
import {ENTITY_PERFORMANCE_CHART_VIEW_OPTIONS} from '../utils'
import {generateColorsFromIndex} from '../../../utils/chartColors'

type ReportType =
  | 'customer_group'
  | 'customer_type'
  | 'sales_rep'
  | 'sales_rep_type'
  | 'supplier'
  | 'classification'
  | 'purchaser_by_supplier'
  | 'brand'
  | 'purchaser_by_brand'
  | 'all'

const ENTITY_PARAM_MAP: Record<ReportType, string> = {
  customer_group: 'customer_group_id',
  customer_type: 'customer_type',
  sales_rep: 'rep_email',
  sales_rep_type: 'sales_rep_type',
  supplier: 'supplier_name',
  classification: 'classification',
  purchaser_by_supplier: 'purchaser_name',
  brand: 'brand_id',
  purchaser_by_brand: 'purchaser_name',
  all: ' ',
}

const useGetEntityPerformanceChartData = (reportType: ReportType) => {
  const Api = useApi()
  const lastEntityValue = useRef<string | null>(null)

  const initialFilters = {
    start_date: moment().subtract(6, 'days').format('YYYY-MM-DD'),
    end_date: moment().format('YYYY-MM-DD'),
    period: 'day',
    report_type: reportType,
  }

  const {filters, setSingleFilter, setMultipleFilters} = useFilters(initialFilters)

  const {data: response, isFetching: isLoading} = Api.useGetQuery(
    '/analytics/profitability/charts',
    {
      queryId: `profitability-chart-${reportType}`,
      filters: filters,
      isToast: false,
      isErrorPageRedirection: false,
    },
    {
      enabled: !!filters?.[ENTITY_PARAM_MAP[reportType]],
    }
  )
  const entityPerformanceChartData = (() => {
    if (!response?.data?.datasets?.length) return {labels: [], datasets: []}

    let {labels, datasets} = response.data

    let newLabels = []

    if (filters.period === 'month') {
      labels.sort((a: any, b: any) => {
        const [aMonth, aYear] = a.split('_').map(Number)
        const [bMonth, bYear] = b.split('_').map(Number)
        return aYear !== bYear ? aYear - bYear : aMonth - bMonth
      })

      newLabels = labels.map((label: any) => {
        const [month, year] = label.split('_').map(Number)
        return `${MONTHS_SHORT[month - 1]} ${year}`
      })
    } else if (filters.period === 'day') {
      newLabels = labels.map((label: any) => label.split('_')[0])
    } else {
      newLabels = labels
    }

    const chartDatasets: any[] = []

    // Add all existing datasets directly
    datasets
      ?.filter((dataset: any) => dataset?.label !== 'Orders')
      .forEach((dataset: any, index: number) => {
        chartDatasets.push({
          label: dataset.label,
          data: dataset.data,
          borderColor: generateColorsFromIndex(index),
          backgroundColor: generateColorsFromIndex(index),
          fill: false,
        })
      })

    return {
      labels: newLabels,
      datasets: chartDatasets,
    }
  })()

  const onEntityPerformanceChartDateRangeChange = (startDate: any, endDate: any) => {
    setMultipleFilters({
      start_date: startDate?.format('YYYY-MM-DD'),
      end_date: endDate?.format('YYYY-MM-DD'),
      period: ENTITY_PERFORMANCE_CHART_VIEW_OPTIONS(
        startDate?.format('YYYY-MM-DD'),
        endDate?.format('YYYY-MM-DD')
      )[0].value,
    })
  }

  const onEntityPerformanceChartIdChange = useCallback(
    (entityValue: string) => {
      lastEntityValue.current = entityValue
      setMultipleFilters({
        [ENTITY_PARAM_MAP[reportType]]: entityValue === 'All' ? ' ' : entityValue,
      })
    },
    [reportType, setMultipleFilters]
  )

  const onEntityPerformanceChartViewChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSingleFilter('period', e.target.value)
  }

  return {
    entityPerformanceChartData,
    isLoading,
    onEntityPerformanceChartIdChange,
    onEntityPerformanceChartViewChange,
    onEntityPerformanceChartDateRangeChange,
    entityPerformanceChartFilters: filters,
  }
}

export default useGetEntityPerformanceChartData
